import { AccumulatedStatsType } from './accumulated-stats';

export interface DateRange {
    id: string,
    label: string,
    startDate: Date,
    endDate: Date,
    type: AccumulatedStatsType,
}

export function getPredefinedDateRangeList() : DateRange[] {
    return [
        { id: 'TODAY', label: `Today`, startDate: day().start, endDate: day().end, type: AccumulatedStatsType.day },
        { id: 'THIS_WEEK', label: `This week`, startDate: week(0).start, endDate: day().end, type: AccumulatedStatsType.day },
        { id: 'LAST_WEEK', label: `Last week`, startDate: week(-1).start, endDate: week(-1).end, type: AccumulatedStatsType.day },
        { id: 'LAST_MONTH', label: `Last month`, startDate: month(-1).start, endDate: month(-1).end, type: AccumulatedStatsType.day },
        { id: 'LAST_30_DAYS', label: `Previous 30 days`, startDate: day(-30).start, endDate: day().end, type: AccumulatedStatsType.day },
        { id: 'LAST_90_DAYS', label: `Previous 90 days`, startDate: day(-90).start, endDate: day().end, type: AccumulatedStatsType.day },
        { id: 'LAST_12_MONTH', label: `Previous 12 months`, startDate: month(-12).start, endDate: day().end, type: AccumulatedStatsType.month },
        { id: 'THIS_YEAR', label: `This year`, startDate: year(0).start, endDate: day().end, type: AccumulatedStatsType.month },
        { id: 'LAST_YEAR', label: `Last year`, startDate: year(-1).start, endDate: year(-1).end, type: AccumulatedStatsType.month },
        { id: 'LAST_5_YEARS', label: `Last 5 years`, startDate: year(-5).start, endDate: day().end, type: AccumulatedStatsType.year },
        { id: 'CUSTOM', label: `Custom`, startDate: now(), endDate: now(), type: AccumulatedStatsType.day },
    ];
}

/**
 * Get period date that starts now
 */
function now(): Date {
    let now = new Date();
    // OBS: Angular calendar uses local time - not UTC
    now.setHours(0);
    now.setMinutes(0);
    now.setSeconds(0);
    now.setMilliseconds(0);
    return now;
}

/**
 * Get a period that starts at the specified day offset (relative to now) and ends today midnight
 */
export function day(dayOffset: number = 0): { start: Date, end: Date; } {
    const end = now();
    // Init start on end date
    const start = new Date(end.getTime());
    // Set start day to day offset
    start.setDate(end.getDate() + dayOffset);
    end.setDate(end.getDate() + dayOffset + 1);
    end.setMilliseconds(-1);
    return { start, end };
}
 
/**
 * Get a week period (7 days) that starts monday at the specified week offset (relative to today / this week)
 * and ends one week after (on sunday 23:59:59.999)
 */
function week(weekOffset: number = 0): { start: Date, end: Date; } {
    let now = day().start;
    // Find date of previous monday
    const mondayDate = (now.getDate() + weekOffset * 7) - now.getDay() + (now.getDay() == 0 ? -6 : 1);
    const start = new Date(now.setDate(mondayDate));
    // Get end of week
    let end = weekOffset == 0 ? now : new Date(now.setDate(start.getDate() + 7));
    // Get end of the day at 23:59:59.999
    end.setMilliseconds(-1);
    return { start, end };
}

/**
 * Get a month period that starts at the specified month offset (relative to today / this month)
 * and at the last day of the month (at 23:59:59.999)
 */
export function month(monthOffset: number = 0): { start: Date, end: Date; } {
    let now = day().start;
    // Start at first day of month
    let start = new Date(now.setDate(1));
    start.setMonth(start.getMonth() + monthOffset);
    // Get start of month
    let end = monthOffset == 0 ? now : new Date(now.setDate(1));
    // Get end of the month at 23:59:59.999
    end.setMilliseconds(-1);
    return { start, end };
}

/**
 * Get a year period that starts at the specified year offset (relative to today / this year)
 * and at the last day of the year the 31th of december (at 23:59:59.999)
 */
export function year(yearOffset: number = 0): { start: Date, end: Date; } {
    let now = day().start;
    // Start at first day of month
    let start = new Date(now.setDate(1));
    start.setMonth(0);
    start.setFullYear(start.getFullYear() + yearOffset);
    let end = new Date(start.getTime());
    // Get end of the year at 23:59:59.999
    end.setFullYear(end.getFullYear() + 1);
    end.setMilliseconds(-1);
    // Get start of next year
    end = (yearOffset == 0) ? now : end;
    return { start, end };
}
